/**
 * Members — searchable directory of anglers.
 * Route: /members
 *
 * Live (debounced) substring search over usernames. Suspended/test users
 * are excluded server-side. Each result is a rich, tappable card:
 *   - avatar + username + Elite badge
 *   - catch count + personal best
 *   - up to 3 recent catch thumbnails
 *   - Follow / Following button (instant optimistic toggle)
 *
 * Layout is mobile-first: the card stacks below 480px so thumbnails get
 * the full width, and the Follow button drops to its own row on small
 * screens.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Search, Users, Crown, Trophy, Loader2, X, UserPlus, UserCheck } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import CatchImage from '../components/CatchImage';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Avatar = ({ row }) => {
  if (row.avatar_url) {
    return (
      <img
        src={row.avatar_url}
        alt={row.username}
        className="w-12 h-12 rounded-full object-cover border border-[#1f221f]"
        onError={(e) => { e.currentTarget.style.display = 'none'; }}
      />
    );
  }
  const initial = (row.username || '?').charAt(0).toUpperCase();
  return (
    <div className="w-12 h-12 rounded-full bg-[#141714] border border-[#1f221f] flex items-center justify-center text-[#c8b273] font-bold">
      {initial}
    </div>
  );
};

export default function Members() {
  const { token, user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [followBusy, setFollowBusy] = useState(null);
  const debounce = useRef(null);

  const runSearch = useCallback(async (q) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/members/search`, {
        params: { q: q || '', limit: 30 },
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setResults(r.data.members || []);
      setTotal(r.data.total ?? 0);
    } catch {
      // Anonymous calls now succeed too; only network errors hit here.
      toast.error('Could not load anglers');
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Initial load: show most recent joiners.
  useEffect(() => { runSearch(''); }, [runSearch]);

  // Debounced live search.
  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => runSearch(query), 250);
    return () => debounce.current && clearTimeout(debounce.current);
  }, [query, runSearch]);

  const toggleFollow = async (e, member) => {
    // Stop the card navigation from racing the button click.
    e.preventDefault();
    e.stopPropagation();
    if (!token) {
      toast.error('Sign in to follow anglers');
      navigate('/login');
      return;
    }
    if (followBusy === member.id) return;
    setFollowBusy(member.id);
    // Optimistic update first — flip the local state and revert on failure.
    const wasFollowing = !!member.is_following;
    setResults((prev) => prev.map((m) => (
      m.id === member.id ? { ...m, is_following: !wasFollowing } : m
    )));
    try {
      if (wasFollowing) {
        await axios.delete(`${API}/users/${member.id}/follow`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.message(`Unfollowed ${member.username}`);
      } else {
        await axios.post(`${API}/users/${member.id}/follow`, {}, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success(`Following ${member.username}`);
      }
    } catch (err) {
      // Revert optimistic flip
      setResults((prev) => prev.map((m) => (
        m.id === member.id ? { ...m, is_following: wasFollowing } : m
      )));
      toast.error(err.response?.data?.detail || 'Could not update follow');
    } finally {
      setFollowBusy(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#0b0d0b] px-4 py-8 md:py-12" data-testid="members-page">
      <div className="max-w-3xl mx-auto">
        <div className="mb-6">
          <p className="text-xs uppercase tracking-[0.3em] text-[#c8b273] mb-1">
            <Users className="inline-block w-3 h-3 mr-1.5 -mt-0.5" />
            Discover
          </p>
          <h1 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-extrabold uppercase tracking-wider text-white">
            Anglers
          </h1>
          <p className="text-[#8a948a] text-sm mt-1">
            Search by username, tap any card to open their profile.
          </p>
        </div>

        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6b7d6b]" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by username..."
            className="pl-9 pr-10"
            data-testid="member-search-input"
          />
          {query && (
            <button
              onClick={() => setQuery('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[#6b7d6b] hover:text-white"
              data-testid="member-search-clear"
              aria-label="Clear search"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {loading && results.length === 0 ? (
          <div className="glass-card p-10 flex items-center justify-center" data-testid="members-loading">
            <Loader2 className="w-6 h-6 text-[#c8b273] animate-spin" />
          </div>
        ) : results.length === 0 ? (
          <div className="glass-card p-12 text-center" data-testid="members-empty">
            <Users className="w-10 h-10 text-[#4a504a] mx-auto mb-3" />
            <p className="text-white font-semibold">No anglers found</p>
            <p className="text-[#8a948a] text-sm mt-1">Try a shorter or different search.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="members-list">
            <p className="text-xs uppercase tracking-wider text-[#8a948a] px-1 mb-2">
              {query ? `${results.length} of ${total} matching` : `Latest ${results.length} of ${total}`}
            </p>
            {results.map((m) => {
              const isMe = currentUser?.id === m.id || m.is_self;
              const following = !!m.is_following;
              return (
                <Link
                  key={m.id}
                  to={`/u/${m.id}`}
                  className="block glass-card p-4 hover:border-[#c8b273] transition-colors"
                  data-testid={`member-row-${m.id}`}
                >
                  {/* Header row: avatar + name + stats + follow */}
                  <div className="flex items-start gap-3">
                    <Avatar row={m} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-white font-bold truncate" data-testid={`member-name-${m.id}`}>
                          {m.username}
                        </span>
                        {m.is_elite && (
                          <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-[#c8b273]/15 text-[#c8b273] border border-[#c8b273]/30 font-bold uppercase tracking-wider">
                            <Crown className="w-3 h-3" /> Elite
                          </span>
                        )}
                      </div>
                      {(m.location || m.bio) && (
                        <p className="text-[#6b7d6b] text-xs truncate mt-0.5">{m.location || m.bio}</p>
                      )}
                      <div className="flex items-center gap-4 mt-1.5 text-xs">
                        <span className="text-[#c0c4c0]">
                          <span className="text-[#8a948a] uppercase tracking-wider text-[10px]">Catches</span>{' '}
                          <span className="font-bold text-white">{m.catch_count ?? 0}</span>
                        </span>
                        {m.pb_weight ? (
                          <span className="text-[#c8b273] flex items-center gap-1">
                            <Trophy className="w-3 h-3" />
                            <span className="font-bold">{m.pb_weight}lb</span>
                            {m.pb_fish_type && (
                              <span className="text-[#6b7d6b]">· {m.pb_fish_type}</span>
                            )}
                          </span>
                        ) : null}
                      </div>
                    </div>
                    {!isMe && (
                      <Button
                        size="sm"
                        variant={following ? 'outline' : 'default'}
                        className={following
                          ? 'border-[#c8b273]/40 text-[#c8b273] hover:bg-[#c8b273]/10'
                          : 'btn-gold'}
                        onClick={(e) => toggleFollow(e, m)}
                        disabled={followBusy === m.id}
                        data-testid={`member-follow-btn-${m.id}`}
                      >
                        {followBusy === m.id ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : following ? (
                          <><UserCheck className="w-3.5 h-3.5 mr-1" />Following</>
                        ) : (
                          <><UserPlus className="w-3.5 h-3.5 mr-1" />Follow</>
                        )}
                      </Button>
                    )}
                  </div>

                  {/* Thumbnail strip — last 3 catches */}
                  {m.recent_catches && m.recent_catches.length > 0 && (
                    <div className="mt-3 grid grid-cols-3 gap-1.5" data-testid={`member-thumbs-${m.id}`}>
                      {m.recent_catches.map((c) => (
                        <div
                          key={c.id}
                          className="relative aspect-square overflow-hidden rounded border border-[#1f221f]"
                        >
                          <CatchImage row={c} aspectRatio="square" className="w-full h-full" />
                          {c.weight && (
                            <span className="absolute bottom-1 right-1 text-[10px] font-bold text-[#c8b273] bg-black/70 px-1.5 py-0.5 rounded">
                              {c.weight}lb
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
